/**
 * One human name per template key (#1911).
 *
 * A `Record` over the union rather than a lookup with a fallback, so a sixth
 * key cannot ship unnamed: a dropdown option reading `notice.delist` is a raw
 * identifier shown to an operator who has to guess which of two
 * nearly-identical drafts they are about to load, and a template list showing
 * five of them is a settings page written in database.
 */
import type { EmailTemplateKey } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';

const TEMPLATE_NAMES: Readonly<Record<EmailTemplateKey, I18nKey>> = {
  'balance_alert.warning': 'email_templates.name.balance_alert_warning',
  'balance_alert.critical': 'email_templates.name.balance_alert_critical',
  'balance_alert.recovered': 'email_templates.name.balance_alert_recovered',
  'notice.delist': 'email_templates.name.notice_delist',
  'notice.list': 'email_templates.name.notice_list',
  'credit.due_soon_7d': 'email_templates.name.credit_due_soon_7d',
  'credit.due_soon_3d': 'email_templates.name.credit_due_soon_3d',
  'credit.due_today': 'email_templates.name.credit_due_today',
  'credit.frozen': 'email_templates.name.credit_frozen',
  'credit.overdue_reminder': 'email_templates.name.credit_overdue_reminder',
  'credit.stopped': 'email_templates.name.credit_stopped',
  'credit.restored': 'email_templates.name.credit_restored',
  'credit.temp_expiring_3d': 'email_templates.name.credit_temp_expiring_3d',
  'credit.temp_expiring_1d': 'email_templates.name.credit_temp_expiring_1d',
  'codex_capacity.elevated': 'email_templates.name.codex_capacity_elevated',
  'codex_capacity.recovered': 'email_templates.name.codex_capacity_recovered',
};

export function templateNameKey(key: EmailTemplateKey): I18nKey {
  return TEMPLATE_NAMES[key];
}

const TEMPLATE_HINTS: Readonly<Record<EmailTemplateKey, I18nKey>> = {
  'balance_alert.warning': 'email_templates.hint.balance_alert_warning',
  'balance_alert.critical': 'email_templates.hint.balance_alert_critical',
  'balance_alert.recovered': 'email_templates.hint.balance_alert_recovered',
  'notice.delist': 'email_templates.hint.notice_delist',
  'notice.list': 'email_templates.hint.notice_list',
  'credit.due_soon_7d': 'email_templates.hint.credit_due_soon_7d',
  'credit.due_soon_3d': 'email_templates.hint.credit_due_soon_3d',
  'credit.due_today': 'email_templates.hint.credit_due_today',
  'credit.frozen': 'email_templates.hint.credit_frozen',
  'credit.overdue_reminder': 'email_templates.hint.credit_overdue_reminder',
  'credit.stopped': 'email_templates.hint.credit_stopped',
  'credit.restored': 'email_templates.hint.credit_restored',
  'credit.temp_expiring_3d': 'email_templates.hint.credit_temp_expiring_3d',
  'credit.temp_expiring_1d': 'email_templates.hint.credit_temp_expiring_1d',
  'codex_capacity.elevated': 'email_templates.hint.codex_capacity_elevated',
  'codex_capacity.recovered': 'email_templates.hint.codex_capacity_recovered',
};

/**
 * What reaches for this row, and when.
 *
 * The three `balance_alert.*` rows are mailed by an unattended cron; the two
 * `notice.*` rows are only ever a starting draft a human then edits. That is
 * the difference between "saving this changes what the platform says on its
 * own" and "saving this changes what the next draft starts as", and it is not
 * visible from the key.
 */
export function templateHintKey(key: EmailTemplateKey): I18nKey {
  return TEMPLATE_HINTS[key];
}
